import React, { useState } from "react";
import Button from "./elements/button";

interface Tab {
  name: string;
  disabled: boolean;
  label?: React.ReactNode;
  count?: number;
}

interface TopTabsProps {
  tabs: Tab[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  pageSize?: number;
}

const TopTabs: React.FC<TopTabsProps> = ({ tabs, activeTab, setActiveTab, pageSize = 6 }) => {
  const [startIndex, setStartIndex] = useState(0);
  const itemsPerPage = pageSize;
  const scrollStep = 1;
  const showNavigation = tabs?.length > itemsPerPage;

  const visibleTabs = showNavigation 
    ? tabs?.slice(startIndex, startIndex + itemsPerPage)
    : tabs;
  const canScrollLeft = startIndex > 0;
  const canScrollRight = startIndex + itemsPerPage < (tabs?.length || 0);

  const handlePrevious = () => {
    setStartIndex((prev) => Math.max(0, prev - scrollStep));
  };

  const handleNext = () => {
    setStartIndex((prev) => Math.min(tabs?.length - itemsPerPage, prev + scrollStep));
  };

  return (
    <div className="border-b border-tertiary flex items-center gap-2">
      {/* Previous Button */}
      {showNavigation && (
        <Button
          type="button"
          onClick={handlePrevious}
          disabled={!canScrollLeft}
          aria-label="Previous tabs"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M15 19l-7-7 7-7"></path>
          </svg>
        </Button>
      )}

      {/* Tabs Container */}
      <div className="flex-1 overflow-hidden">
        <nav className={`grid grid-cols-${itemsPerPage} gap-4`}>
          {visibleTabs?.map((tab) => (
            <button
              type="button"
              key={tab.name}
              onClick={() => !tab.disabled && setActiveTab(tab.name)}
              className={`pb-4 text-md font-medium relative break-words flex-1 ${
                tab.disabled
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-900"
              } max-w-[250px]`}
              disabled={tab.disabled}
            >
              {tab.label || tab.name}
              {tab.count !== undefined && ` (${tab.count})`}
              {activeTab === tab.name && !tab.disabled && (
                <span className="absolute left-0 bottom-[0px] w-full h-1 bg-secondary"></span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Next Button */}
      {showNavigation && (
        <Button
          type="button"
          onClick={handleNext}
          disabled={!canScrollRight}
          aria-label="Next tabs"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M9 5l7 7-7 7"></path>
          </svg>
        </Button>
      )}
    </div>
  );
};

export default TopTabs;
